/**
 * llmClient.ts — Provider-agnostischer Dispatcher für LLM-Vorschläge.
 *
 * Entscheidet zur Laufzeit ob Mistral (EU, Paris) oder Anthropic (Claude)
 * aufgerufen wird, basierend auf ENV-Konfiguration:
 *
 *   VITE_LLM_PROVIDER=anthropic|mistral  (optional, default: anthropic
 *     falls anthropic-key gesetzt, sonst mistral)
 *   VITE_ANTHROPIC_API_KEY=sk-ant-...    (für Claude)
 *   VITE_MISTRAL_API_KEY=...             (für Mistral)
 *
 * Aufrufer (UI) ruft `callLlm(systemPrompt, userPrompt, opts)` und bekommt
 * eine einheitliche Response, unabhängig vom Provider. Fehler werden als
 * `LlmError` gemappt — die UI braucht nur ein Error-Mapping statt zwei.
 *
 * Das vereinfacht den UI-Code + ermöglicht später einfaches Hinzufügen
 * weiterer Provider (z.B. Aleph Alpha, eigener On-Prem) ohne Refactor.
 */

import { callAnthropic, AnthropicError, isAnthropicConfigured } from './anthropicClient';
import { callMistral, MistralError, isMistralConfigured } from './mistralClient';
import type { LlmProvider } from '../supabaseTypes';

// --- Provider-Erkennung ---------------------------------------------------

export type LlmProviderName = 'anthropic' | 'mistral';

/**
 * Client-seitiger Anthropic-Pfad ist HART DEAKTIVIERT (Security-Entscheidung
 * 2026-07-15).
 *
 * Gründe:
 *   1. api.anthropic.com ist US-Hosting — für Art.-9-Sozialdaten ohne AVV +
 *      DSB-Freigabe unzulässig (CLAUDE.md: „kein US-Service in der
 *      Verarbeitungskette", „Anthropic via Gateway").
 *   2. Der Aufruf setzt `anthropic-dangerous-direct-browser-access` und legt
 *      den API-Key als VITE_-Variable ins öffentliche Bundle (extrahierbar).
 *
 * Bis ein serverseitiger EU-Proxy (Supabase Edge Function / Cloudflare Worker)
 * steht, darf client-seitig ausschließlich Mistral (EU, Paris) aktiv sein.
 * Der Anthropic-Code bleibt für den späteren Proxy-Umbau erhalten, wird aber
 * hier nie ausgewählt.
 */
const ANTHROPIC_CLIENT_ENABLED = false;

/**
 * Liefert den aktiven Provider basierend auf ENV-Konfiguration.
 *
 * Priorität:
 *   1. Wenn VITE_LLM_PROVIDER explizit gesetzt → dieser
 *   2. Sonst: mistral wenn dessen Key gesetzt, sonst null
 *
 * Anthropic ist client-seitig deaktiviert (siehe ANTHROPIC_CLIENT_ENABLED).
 */
export function getActiveProvider(): LlmProviderName | null {
  const env = (typeof import.meta !== 'undefined' ? import.meta.env : undefined) as
    | Record<string, string | undefined>
    | undefined;
  const explicit = env?.VITE_LLM_PROVIDER?.trim().toLowerCase();
  if (ANTHROPIC_CLIENT_ENABLED && explicit === 'anthropic' && isAnthropicConfigured())
    return 'anthropic';
  if (explicit === 'mistral' && isMistralConfigured()) return 'mistral';
  // Auto-fallback (nur EU-Provider)
  if (ANTHROPIC_CLIENT_ENABLED && isAnthropicConfigured()) return 'anthropic';
  if (isMistralConfigured()) return 'mistral';
  return null;
}

/**
 * @returns true wenn IRGENDEIN Provider konfiguriert ist.
 */
export function isLlmConfigured(): boolean {
  return getActiveProvider() !== null;
}

/**
 * Mensch-lesbarer Name für UI ("Claude Sonnet 4.6 (Anthropic)" etc.).
 */
export function getActiveProviderLabel(): string {
  const provider = getActiveProvider();
  if (provider === 'anthropic') return 'Claude Sonnet (Anthropic)';
  if (provider === 'mistral') return 'Mistral Large (Paris, EU)';
  return 'kein EU-Provider konfiguriert';
}

/**
 * Default-Modell pro Provider. Aufrufer kann via opts.model überschreiben.
 */
export function getDefaultModel(provider: LlmProviderName): LlmProvider {
  if (provider === 'anthropic') return 'claude-sonnet-4-6';
  return 'mistral-large-2.5';
}

// --- Vereinheitlichte Types ----------------------------------------------

export const LlmErrorCode = {
  LLM_NOT_CONFIGURED: 'llm_not_configured',
  LLM_FLAG_DISABLED: 'llm_flag_disabled',
  PII_DETECTED: 'pii_detected',
  HTTP_ERROR: 'http_error',
  TIMEOUT: 'timeout',
  PARSE_ERROR: 'parse_error',
  PROVIDER_UNKNOWN: 'provider_unknown',
} as const;

export type LlmErrorCodeValue = (typeof LlmErrorCode)[keyof typeof LlmErrorCode];

export class LlmError extends Error {
  public readonly code: LlmErrorCodeValue;
  public readonly httpStatus: number | undefined;
  public readonly provider: LlmProviderName | null;

  constructor(
    code: LlmErrorCodeValue,
    message: string,
    provider: LlmProviderName | null,
    httpStatus?: number,
  ) {
    super(message);
    this.code = code;
    this.provider = provider;
    this.httpStatus = httpStatus;
    this.name = 'LlmError';
  }
}

export interface LlmCallOptions {
  /** Optional: explizites Modell. Sonst Default des aktiven Providers. */
  readonly model?: LlmProvider;
  readonly temperature?: number;
  readonly maxTokens?: number;
  readonly caseId?: string | null;
  readonly fieldKey: string;
  readonly fetchImpl?: typeof fetch;
}

export interface LlmResponse {
  readonly content: string;
  readonly promptTokens: number;
  readonly completionTokens: number;
  readonly totalTokens: number;
  readonly durationMs: number;
  readonly provider: LlmProviderName;
  readonly model: LlmProvider;
}

/**
 * Hauptaufruf — provider-agnostisch.
 *
 * Wirft `LlmError` mit programmatisch auswertbarem `code` (für UI-Mapping).
 */
export async function callLlm(
  systemPrompt: string,
  userPrompt: string,
  opts: LlmCallOptions,
): Promise<LlmResponse> {
  const provider = getActiveProvider();
  if (!provider) {
    throw new LlmError(
      LlmErrorCode.LLM_NOT_CONFIGURED,
      'Kein LLM-Provider konfiguriert. Client-seitig ist nur der EU-Provider (Mistral) zulässig; der Key gehört serverseitig hinter einen EU-Proxy, nicht ins Browser-Bundle.',
      null,
    );
  }

  const model = opts.model ?? getDefaultModel(provider);

  // exactOptionalPropertyTypes: undefined-Felder explizit weglassen statt
  // mit undefined zu setzen. Helper-Object zum spread-mergen nur gesetzter
  // Optionalen.
  const optionalOpts: Record<string, unknown> = {};
  if (opts.temperature !== undefined) optionalOpts.temperature = opts.temperature;
  if (opts.maxTokens !== undefined) optionalOpts.maxTokens = opts.maxTokens;
  if (opts.caseId !== undefined) optionalOpts.caseId = opts.caseId;
  if (opts.fetchImpl !== undefined) optionalOpts.fetchImpl = opts.fetchImpl;

  if (provider === 'anthropic') {
    try {
      const resp = await callAnthropic(systemPrompt, userPrompt, {
        model,
        fieldKey: opts.fieldKey,
        ...optionalOpts,
      });
      return { ...resp, provider, model };
    } catch (e) {
      if (e instanceof AnthropicError) {
        throw new LlmError(mapAnthropicErrorCode(e.code), e.message, provider, e.httpStatus);
      }
      throw e;
    }
  }

  if (provider === 'mistral') {
    try {
      const resp = await callMistral(systemPrompt, userPrompt, {
        model,
        fieldKey: opts.fieldKey,
        ...optionalOpts,
      });
      return { ...resp, provider, model };
    } catch (e) {
      if (e instanceof MistralError) {
        throw new LlmError(mapMistralErrorCode(e.code), e.message, provider, e.httpStatus);
      }
      throw e;
    }
  }

  throw new LlmError(
    LlmErrorCode.PROVIDER_UNKNOWN,
    `Unbekannter Provider: ${String(provider)}`,
    null,
  );
}

// --- Error-Code-Mapping (intern) -----------------------------------------

function mapAnthropicErrorCode(code: string): LlmErrorCodeValue {
  switch (code) {
    case 'anthropic_not_configured':
      return LlmErrorCode.LLM_NOT_CONFIGURED;
    case 'llm_flag_disabled':
      return LlmErrorCode.LLM_FLAG_DISABLED;
    case 'pii_detected':
      return LlmErrorCode.PII_DETECTED;
    case 'http_error':
      return LlmErrorCode.HTTP_ERROR;
    case 'timeout':
      return LlmErrorCode.TIMEOUT;
    case 'parse_error':
      return LlmErrorCode.PARSE_ERROR;
    default:
      return LlmErrorCode.HTTP_ERROR;
  }
}

function mapMistralErrorCode(code: string): LlmErrorCodeValue {
  switch (code) {
    case 'mistral_not_configured':
      return LlmErrorCode.LLM_NOT_CONFIGURED;
    case 'llm_flag_disabled':
      return LlmErrorCode.LLM_FLAG_DISABLED;
    case 'pii_detected':
      return LlmErrorCode.PII_DETECTED;
    case 'http_error':
      return LlmErrorCode.HTTP_ERROR;
    case 'timeout':
      return LlmErrorCode.TIMEOUT;
    case 'parse_error':
      return LlmErrorCode.PARSE_ERROR;
    default:
      return LlmErrorCode.HTTP_ERROR;
  }
}
